<template>
  <article
    v-if="page"
    class="page"
    ref="page"
    :class="{
      'page--show': loaded
    }"
    @click="toggleActive()">
    <section
      v-if="$route.path !== '/'"
      class="page__navigation">
      <h2
        class="page__navigation-title"
        v-html="page.name"></h2>
      <nav class="nav">
        <ul class="nav__list">
          <li
            v-for="(section, key) in filterArticles(page.articles)"
            class="nav__list-item"
            :key="`content${key}`">
            <a
              :href="`#${section.slug}`"
              @click.prevent="scrollToContent(section)">
              <h3 v-html="section.name"></h3>
            </a>
          </li>
        </ul>
      </nav>
    </section>
    <section
      v-for="(article, key) in page.articles"
      class="section"
      :id="`${article.name.toLowerCase().replace(/\s+/g, '-')}`"
      :class="[
        `section-${article.id}`,
        {
          'section--first': key === 0
        }
      ]"
      :key="`section${key}`">

      <a
        class="section__anchor"
        :href="`#${article.name.toLowerCase().replace(/\s+/g, '-')}`"></a>

      <div
        v-for="(content, key) in article.content"
        class="content"
        :class="`content--${content.type}`"
        :key="`content${key}`">

        <Heading
          v-if="content.type === 'heading'"
          :text="content.value.text"
          :size="content.value.size" />

        <ImageBlock
          v-if="content.type === 'image'"
          :image="content.value.image"
          :caption="content.value.caption"
          :width="content.value.width"
          :align="content.value.align"
          :include-highlight="content.value.highlight" />

        <Gallery
          v-if="content.type === 'gallery'"
          :images="content.value" />

        <LinkedGallery
          v-if="content.type === 'linked_gallery'"
          :images="content.value.images"
          :width="content.value.width"
          :columns="content.value.columns"
          :note="content.value.note" />

        <TableBlock
          v-if="content.type === 'table'"
          :cols="content.value.cols"
          :rows="content.value.rows" />

        <Wysiwyg
          v-if="content.type === 'wysiwyg'"
          :content="content.value" />

        <AsideBlock
          v-if="content.type === 'aside'"
          :text="content.value.text"
          :position="content.value.position" />

        <Space
          v-if="content.type === 'space'"
          :size="content.value" />

        <Quote
          v-if="content.type === 'quote'"
          :quote="content.value.text"
          :size="content.value.size" />

        <MapBlock
          v-if="content.type === 'map'"
          :map="content.value.map"
          :colour="content.value.colour"
          :includeAddress="content.value.include_address"
          :includeMapLink="content.value.include_map"
          :locations="content.value.locations" />

        <InteractiveBlock
          v-if="content.type === 'interactive'"
          :highlights="content.value.highlights"
          :artwork="content.value.artwork"
          :ratio="parseFloat(content.value.artwork.height / content.value.artwork.width).toFixed(3)" />

        <InteractiveList
          v-if="content.type === 'interactive_list'"
          :items="content.value.items"
          :artwork="content.value.artwork"
          :ratio="parseFloat(content.value.artwork.height / content.value.artwork.width).toFixed(3)" />

        <HighlightList
          v-if="content.type === 'highlight_list'"
          :rows="content.value.rows" />

        <Dropdown
          v-if="content.type === 'dropdown'"
          :rows="content.value.rows" />

        <EventList
          v-if="content.type === 'event_list' || content.type === 'select_events'"
          :title="content.value.title"
          :description="content.value.description"
          :events="content.value.events" />

        <FormBlock v-if="content.type === 'form'" />
      </div>
    </section>

  </article>
</template>

<script>
import Heading from '@/components/base/Heading'
import ImageBlock from '@/components/base/ImageBlock'
import Gallery from '@/components/base/Gallery'
import LinkedGallery from '@/components/base/LinkedGallery'
import TableBlock from '@/components/base/TableBlock'
import Wysiwyg from '@/components/base/Wysiwyg'
import AsideBlock from '@/components/base/Aside'
import Space from '@/components/base/Space'
import Quote from '@/components/base/Quote'
import MapBlock from '@/components/base/MapBlock'
import InteractiveBlock from '@/components/base/Interactive'
import InteractiveList from '@/components/base/InteractiveList'
import HighlightList from '@/components/base/HighlightList'
import Dropdown from '@/components/base/Dropdown'
import EventList from '@/components/base/EventList'
import FormBlock from './base/FormBlock'

export default {
  name: 'Page',
  components: {
    FormBlock,
    Heading,
    ImageBlock,
    Gallery,
    LinkedGallery,
    TableBlock,
    Wysiwyg,
    AsideBlock,
    Space,
    Quote,
    MapBlock,
    InteractiveBlock,
    InteractiveList,
    HighlightList,
    Dropdown,
    EventList
  },
  props: {
    default: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      page: null,
      loaded: false,
      contents: null
    }
  },
  watch: {
    '$route.path': async function(path) {
      if (path) {
        this.loaded = false

        this.page = await this.getPage()

        this.$root.$emit('updateCover', this.page.media)

        setTimeout(() => {
          window.scrollTo(0, 0)

          this.loaded = true

          if (location.hash) {
            const location = document.querySelector(`${location.hash}`)
            const header = document.querySelector('.header')

            const scrollPosition = location.offset.top + header.offset.top

            window.scrollTo(0, scrollPosition)
          }

          setTimeout(() => this.$emit('switchToPage', false), 0)
        }, 300)
      }
    }
  },
  async created() {
    this.page = await this.getPage()

    setTimeout(() => {
      window.scrollTo(0, 0)

      this.loaded = true

      if (location.hash) {
        const location = document.querySelector(`${location.hash}`)
        const header = document.querySelector('.header')

        const scrollPosition = location.offset.top + header.offset.top

        window.scrollTo(0, scrollPosition)
      }
    }, 300)
  },
  methods: {
    getPage() {
      // get page ~ is route or selected page content in siteContent.
      let page = this.$route.path
      let pageUrl = `${process.env.VUE_APP_API_ADDRESS}/page/base`

      // return page
      return new Promise(resolve => {
        fetch(pageUrl, {
          method: 'POST',
          headers: this.$headers,
          body: JSON.stringify({ page: page })
        })
        .then(response => response.json())
        .then(result => resolve(result))
      })
    },
    scrollToContent(section) {
      const sectionElement = document.querySelector(`.section-${section.id}`)
      const sectionBounds = sectionElement.getBoundingClientRect()

      const contentMargin = ((window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) < 769) ? (55 + 10) : (75 + 20)

      window.scrollTo({
        top: sectionBounds.top - contentMargin,
        left: 0,
        behavior: 'smooth'
      })
    },
    toggleActive() {
      this.$emit('toggleActive', false)
    },
    filterArticles(articles) {
      return articles.filter(article => {
        return article.name !== ''
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  padding: $page-padding;
  z-index: 1;
  background-color: $background;
  transition: width 100ms ease-in-out, margin 100ms ease-in-out;
  transition-delay: 200ms;
  width: 100%;
  max-width: $content-width;
  margin: 104px 0 0 0;

  &--show {
    .section {
      opacity: 1 !important;
      transition-delay: 100ms;
      transition-duration: 200ms;
    }
  }

  &__navigation {
    margin:0 0 #{$padding * 4} 0;
    display: flex;
    flex-wrap: wrap;

    &-title {
      width: 100%;
      color: $black;
      margin: 0 0 10px 0;
    }

    .nav {
      &__list {
        &-item {
          a {
            padding: 6px 16px;
            border: 3px solid $black;
            display: inline-block;
            margin: 0 0 10px 0;
            border-radius:20px;
            &:hover {
              color: $white;
              background-color: $black;
            }
          }
        }
      }
    }
  }

  .section {
    opacity:0;
    transition: opacity 200ms ease-in-out;
    margin: calc(100px - #{$page-padding}) 0 0 0;
    padding: $page-padding 0 0 0;

    &--first {
      margin-top: 0px !important;
      padding-top: 0px !important;
    }

    &__anchor {
      opacity: 0;
      z-index: -100;
    }

    h5 {
      text-align: center;
      text-transform: uppercase;
      line-height:1 !important;
    }
  }

  .content {
    margin: 0 0 $page-padding 0;

    &--heading {
      margin: 0;
    }

    &--image {
      width:100%;
      display:flex;
      flex-wrap:wrap;
    }
  }

  @media screen and (max-width: $mobile) {
    margin: calc(55px + #{$padding / 2}) 0;
    padding: #{$padding} $mobile-padding;
    width: 100%;
    overflow-x: hidden;

    &__navigation {
      flex-direction: column;
      margin: 0 0 #{$padding * 2} 0;
      h2 {
        width: 100%;
        display: none;
      }
      .nav__list {
        &-item {
          a {
            border-width: 2px;
            padding: 5px 14px;
            margin: 0 0 6px 0;
          }
        }
      }
    }

    .content {
      margin: 0 0 $padding 0;

      &--table {
        overflow-x: auto;
      }

      &--heading {
        margin: 0px;
      }
    }

    .section {
      margin: calc(60px - #{$padding}) 0 0 0;
      padding: #{$padding / 2} 0 0 0;
    }
  }
}
</style>
